'use client';
import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import { getMIRRef } from '@/lib/demoDate';

interface Props {
  duration: string;
  onCreateCase: (caseId: string) => void;
  onDiscard: () => void;
}

type WrapUpPhase = 'preparing' | 'draft' | 'case-success';

const CASE_ID = 'MYGCC-2026-008432';
const INTERACTION_ID = 'INT-2026-14892';
const MIR_REF = getMIRRef(14892);

export default function AIWrapUp({ duration, onCreateCase, onDiscard }: Props) {
  const [phase, setPhase] = useState<WrapUpPhase>('preparing');
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [summary, setSummary] = useState(
    'Citizen contacted MyGCC regarding updating the registered mobile number associated with their MySejahtera account. Identity information was verified and approved KKM guidance was provided. Citizen confirmed understanding of the required steps.'
  );
  const [fields, setFields] = useState({
    subject: 'MySejahtera Mobile Number Update',
    agency: 'Kementerian Kesihatan Malaysia (KKM)',
    category: 'MySejahtera',
    subcategory: 'Account Information Update',
    intent: 'MySejahtera Mobile Number Update',
    sentiment: 'Satisfied',
    channel: 'Voice',
    mirRecording: MIR_REF,
    issue: 'Citizen requested assistance to update the registered mobile number associated with the MySejahtera account. Identity verification requirements explained.',
    actionTaken: 'Approved KKM guidance on MySejahtera mobile number update procedure provided. Identity verified.',
    disposition: 'Enquiry Resolved',
    priority: 'Medium',
    status: 'Resolved',
    followUp: 'Not Required',
  });

  useEffect(() => {
    const t = setTimeout(() => setPhase('draft'), 2500);
    return () => clearTimeout(t);
  }, []);

  const updateField = (key: keyof typeof fields, value: string) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setEditedFields(prev => new Set(prev).add(key));
  };

  const handleCreateCase = () => {
    setPhase('case-success');
    onCreateCase(CASE_ID);
  };

  const handleDiscard = () => {
    setShowDiscardConfirm(false);
    onDiscard();
    toast.info('AI draft discarded');
  };

  if (phase === 'preparing') {
    return (
      <div className="p-4 flex flex-col items-center justify-center h-full space-y-4 min-h-[300px]">
        <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center">
          <Icon name="SparklesIcon" size={24} className="text-blue-600" />
        </div>
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span key={i} className="w-2.5 h-2.5 bg-primary rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />
          ))}
        </div>
        <div className="text-[14px] font-700 text-primary">Generating Interaction Summary...</div>
        <div className="text-[11px] text-muted-foreground text-center max-w-[260px]">
          CX AI Assist is preparing the interaction summary and case draft from the completed call.
        </div>
        <div className="text-[10px] text-muted-foreground">
          Mitel MIR Recording Finalised · {MIR_REF}
        </div>
      </div>
    );
  }

  if (phase === 'case-success') {
    return (
      <div className="p-4 space-y-4 overflow-y-auto">
        {/* Success header */}
        <div className="flex flex-col items-center text-center py-4">
          <div className="w-14 h-14 rounded-2xl bg-green-50 border border-green-200 flex items-center justify-center mb-3">
            <Icon name="CheckCircleIcon" size={28} className="text-green-600" />
          </div>
          <div className="text-[16px] font-700 text-green-700 mb-1">Case Created Successfully</div>
          <div className="text-[11px] text-muted-foreground">Interaction completed and case recorded</div>
        </div>

        {/* Case ID */}
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 text-center">
          <div className="text-[11px] text-muted-foreground mb-1">Case Reference</div>
          <div className="text-[22px] font-700 font-mono text-primary">{CASE_ID}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Linked Interaction: {INTERACTION_ID}</div>
        </div>

        {/* Case details */}
        <div className="card-elevated p-4 space-y-2.5">
          {[
            { label: 'Citizen', value: 'Ahmed Firdaus' },
            { label: 'Topic', value: fields.subject },
            { label: 'Agency', value: fields.agency },
            { label: 'Channel', value: 'Voice' },
            { label: 'Status', value: 'New' },
            { label: 'Assigned To', value: 'Nur Aisyah' },
            { label: 'MIR Recording', value: MIR_REF },
          ].map(row => (
            <div key={row.label} className="flex items-center justify-between text-[12px]">
              <span className="text-muted-foreground">{row.label}</span>
              <span className="font-600 text-foreground text-right max-w-[200px] truncate">{row.value}</span>
            </div>
          ))}
        </div>

        {/* Interaction association */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-[11px] text-blue-700">
          <div className="font-700 mb-1">Interaction → Case Association</div>
          <div className="font-mono">{INTERACTION_ID} → {CASE_ID}</div>
          <div className="mt-1 text-blue-600">Interaction status updated to: <strong>Completed</strong></div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => toast.info(`Opening case ${CASE_ID}`)}
            className="btn-primary py-2.5 flex items-center justify-center gap-2 text-[13px]"
          >
            <Icon name="ClipboardDocumentListIcon" size={15} />
            View Case
          </button>
          <button
            onClick={() => toast.info('Returning to interactions')}
            className="btn-secondary py-2 text-[12px] flex items-center justify-center gap-1.5"
          >
            <Icon name="ArrowLeftIcon" size={13} />
            Back to Interactions
          </button>
        </div>

        <div className="text-[10px] text-muted-foreground text-center py-1 border-t border-border pt-2">
          AI-generated draft · Reviewed by agent · Case recorded in MyGCC CRM
        </div>
      </div>
    );
  }

  // Draft phase
  return (
    <div className="p-4 space-y-3 overflow-y-auto">
      {/* Discard confirm */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/40">
          <div className="bg-card border border-border rounded-2xl shadow-modal p-5 w-[360px] slide-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
                <Icon name="TrashIcon" size={16} className="text-red-600" />
              </div>
              <div>
                <h2 className="text-[14px] font-700 text-foreground">Discard AI Draft?</h2>
                <p className="text-[11px] text-muted-foreground">The AI-generated draft will be removed.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowDiscardConfirm(false)} className="flex-1 btn-secondary py-2.5 text-[13px]">Cancel</button>
              <button onClick={handleDiscard} className="flex-1 py-2.5 text-[13px] bg-red-600 hover:bg-red-700 text-white rounded-xl font-700 transition-colors">Discard Draft</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2">
        <Icon name="SparklesIcon" size={15} className="text-primary" />
        <h2 className="text-[13px] font-700 text-primary">AI Assisted Wrap-Up</h2>
        <span className="text-[9px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded font-700 ml-auto">AI Draft Ready</span>
      </div>

      {/* Review banner */}
      <div className="bg-amber-50 border border-amber-300 rounded-xl p-2.5 flex items-start gap-2">
        <Icon name="ExclamationTriangleIcon" size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-amber-800">
          <span className="font-700">AI-generated draft · Human review required</span>
          <br />Review all fields before creating the case.
        </div>
      </div>

      {/* Interaction Summary */}
      <div className="card-elevated p-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Interaction Summary</div>
          {editedFields.has('summary') ? (
            <span className="text-[9px] text-blue-600 font-600 bg-blue-50 px-1.5 py-0.5 rounded">Edited by Agent</span>
          ) : (
            <span className="text-[9px] text-primary font-600 bg-primary/10 px-1.5 py-0.5 rounded">✨ AI Generated</span>
          )}
        </div>
        <textarea
          value={summary}
          onChange={e => { setSummary(e.target.value); setEditedFields(prev => new Set(prev).add('summary')); }}
          rows={3}
          className="w-full text-[12px] text-foreground border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
        />
      </div>

      {/* Detected Intent */}
      <div className="card-elevated p-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Detected Intent</div>
          <span className="text-[9px] text-primary font-600 bg-primary/10 px-1.5 py-0.5 rounded">✨ AI Generated</span>
        </div>
        <input
          value={fields.intent}
          onChange={e => updateField('intent', e.target.value)}
          className="w-full text-[13px] font-600 text-foreground border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Classification */}
      <div className="card-elevated p-3 space-y-2.5">
        <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-1">Classification</div>
        {[
          { key: 'subject', label: 'Case Subject' },
          { key: 'agency', label: 'Agency' },
          { key: 'category', label: 'Category' },
          { key: 'subcategory', label: 'Subcategory' },
          { key: 'disposition', label: 'Disposition' },
          { key: 'priority', label: 'Priority' },
          { key: 'status', label: 'Status' },
          { key: 'followUp', label: 'Follow-Up' },
        ].map(f => (
          <div key={f.key} className="flex items-center justify-between gap-2 text-[12px]">
            <span className="text-muted-foreground flex-shrink-0 w-28">{f.label}</span>
            <div className="flex items-center gap-1.5 flex-1 justify-end">
              {editedFields.has(f.key) && (
                <span className="text-[9px] text-blue-600 font-600 bg-blue-50 px-1 py-0.5 rounded flex-shrink-0">Edited</span>
              )}
              <input
                value={fields[f.key as keyof typeof fields]}
                onChange={e => updateField(f.key as keyof typeof fields, e.target.value)}
                className="text-[12px] font-600 text-foreground border border-border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary text-right w-40"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Final Sentiment */}
      <div className="card-elevated p-3">
        <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Final Sentiment</div>
        <div className="flex items-center gap-2 text-[12px]">
          <div className="flex items-center gap-1"><span>😕</span><span className="text-muted-foreground text-[10px]">Concerned</span></div>
          <Icon name="ArrowRightIcon" size={10} className="text-muted-foreground" />
          <div className="flex items-center gap-1"><span>😐</span><span className="text-muted-foreground text-[10px]">Neutral</span></div>
          <Icon name="ArrowRightIcon" size={10} className="text-muted-foreground" />
          <div className="flex items-center gap-1"><span>🙂</span><span className="text-muted-foreground text-[10px] font-700 text-green-700">Satisfied</span></div>
        </div>
      </div>

      {/* MIR Recording Reference */}
      <div className="card-elevated p-3">
        <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-1.5">MIR Recording Reference</div>
        <div className="flex items-center gap-2 text-[12px]">
          <Icon name="CircleStackIcon" size={12} className="text-muted-foreground" />
          <span className="font-600 font-mono">{MIR_REF}</span>
          <span className="text-muted-foreground">· {duration}</span>
          <span className="ml-auto text-[10px] text-green-600 font-600 bg-green-50 px-1.5 py-0.5 rounded">Finalised</span>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1">Voice recording managed by Mitel Communication Foundation</div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-1">
        <button onClick={handleCreateCase} className="btn-primary py-2.5 flex items-center justify-center gap-2 text-[13px]">
          <Icon name="ClipboardDocumentListIcon" size={15} />
          Create Case
        </button>
        <button onClick={handleCreateCase} className="btn-secondary py-2 text-[12px] flex items-center justify-center gap-1.5">
          <Icon name="ArchiveBoxIcon" size={13} />
          Confirm & Resolve
        </button>
        <div className="flex gap-2">
          <button onClick={() => toast.info('Escalation initiated')} className="flex-1 btn-ghost py-2 text-[12px] border border-border flex items-center justify-center gap-1.5">
            <Icon name="ArrowUpIcon" size={13} />
            Escalate
          </button>
          <button onClick={() => setShowDiscardConfirm(true)} className="flex-1 btn-ghost py-2 text-[12px] border border-red-200 text-red-600 flex items-center justify-center gap-1.5">
            <Icon name="TrashIcon" size={13} />
            Discard AI Draft
          </button>
        </div>
      </div>

      <div className="text-[10px] text-muted-foreground text-center py-1 border-t border-border pt-2">
        AI-generated draft · Human review required · Not finalised automatically
      </div>
    </div>
  );
}
